/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';

import AccountService from './account.service';
import BudgetService from './budget.service';
import CashFlowService from './cashflow.service';
import StorageService from './storage.service';
import { AccountModel } from 'src/types/account.model';
import { BudgetModel } from 'src/types/budget.model';
import { CardModel } from 'src/types/card.model';
import { FullUserModel } from 'src/types/user.model';

import { MIXPANEL_HOST, MIXPANEL_KEY } from '../config';
import { ChronoUnit, LocalDate } from '@js-joda/core';

const mixpanel = require('mixpanel-browser');

export class MixpanelUser {
    '$name': string;
    '$first_name': string;
    '$last_name': string;
    '$email': string;
    'Last Login': string;
    Created: string;
    Institutions: number;
    Accounts: number;
    Goals: number;
    'Life Events': string[];
    Income: number;
    Expenses: number;
    Age: string;
    Budgets: string[];
    'Over Budget': number;
    'Under Budget': number;
    'Amex Cards'?: string[];
}

export default class MixpanelService {
    private static readonly NONPROD: boolean =
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('stage') || window.location.hostname === 'localhost');

    private static readonly LOCAL: boolean = typeof window !== 'undefined' && window.location.hostname === 'localhost';

    public static async init(): Promise<void> {
        // dev/stage token
        MIXPANEL_KEY &&
            mixpanel.init(MIXPANEL_KEY, {
                // if null, it uses the default host
                api_host: MIXPANEL_HOST,
                persistence: 'localStorage',
                secure_cookie: true,
                debug: this.LOCAL
            });
    }

    public static track(eventName: string, properties: any = {}): void {
        const user: FullUserModel = StorageService.getReduxUserObject();

        // check for non-production instance using hostname
        if (this.isDemoUser(user)) return;
        if (
            !user &&
            this.NONPROD &&
            // special case, user not set at all, don't send` these events unless in production
            ['S0 - Initial Visit', 'Start Demo', 'Viewed on Mobile'].includes(eventName)
        )
            return;

        // console.log('MIXPANEL track: %s', eventName, properties); // convenient for when adding tracking, keep around
        if (MIXPANEL_KEY) {
            mixpanel.track(eventName, properties);
        }
    }

    private static isDemoUser(user: FullUserModel): boolean {
        return (
            user &&
            (_.get(user, 'attributes.demo') ||
                (this.NONPROD && user.forename === 'Anonymous') ||
                (this.NONPROD && user.email === 'bodeswell.test@gmail.com'))
        );
    }

    public static deleteUser(user: FullUserModel): void {
        // don't track demo user
        if (this.isDemoUser(user)) return;

        try {
            if (MIXPANEL_KEY) {
                mixpanel.track('User Deletion');
                mixpanel.people.increment('Total Deletions');
            }
        } catch (e) {
            console.error('Failed to report to Mixpanel', e);
        }
    }

    public static async loginUser(user: FullUserModel): Promise<void> {
        // don't track demo user
        if (this.isDemoUser(user)) return;

        if (MIXPANEL_KEY) {
            const age: string = user.dob ? user.dob.until(LocalDate.now(), ChronoUnit.YEARS).toString() : 'unknown';

            mixpanel.identify(user.email);
            mixpanel.track('User Login');

            const budgets = await BudgetService.getBudget();
            const categories = budgets.map((budget: BudgetModel) => {
                return budget.groupName;
            });
            const overBudget = _.filter(budgets, (budget: BudgetModel) => budget.currentlySpent > budget.monthlyAmount);
            const underBudget = _.filter(
                budgets,
                (budget: BudgetModel) => budget.currentlySpent <= budget.monthlyAmount
            );

            const userRecord: MixpanelUser = {
                $name: user.forename + ' ' + user.surname,
                $first_name: user.forename,
                $last_name: user.surname,
                $email: user.email,
                'Last Login': user.last_login,
                Created: user.created,
                Institutions: user.institutions.length,
                Accounts: user.accounts ? user.accounts.length : 0,
                Goals: user.cards.length,
                'Life Events': user.cards.map((card: CardModel) => card.description),
                Income: CashFlowService.getCurrentIncome(user),
                Expenses: CashFlowService.getCurrentExpenses(user),
                Age: age,
                Budgets: categories,
                'Over Budget': overBudget.length,
                'Under Budget': underBudget.length
            };

            mixpanel.people.set(userRecord);
            mixpanel.people.increment('Total Logins');
        }
    }

    public static newUser(user: FullUserModel): void {
        // don't track demo user
        if (this.isDemoUser(user)) return;

        if (MIXPANEL_KEY) {
            const age: string = user.dob ? user.dob.until(LocalDate.now(), ChronoUnit.YEARS).toString() : 'unknown';

            mixpanel.alias(user.email);
            mixpanel.track('User Registration');
            mixpanel.people.set({
                $name: user.forename + ' ' + user.surname,
                $first_name: user.forename,
                $last_name: user.surname,
                $email: user.email,
                'Last Login': user.last_login,
                Created: user.created,
                Institutions: user.institutions.length,
                Accounts: user.accounts ? user.accounts.length : 0,
                Goals: user.cards.length,
                'Life Events': user.cards.map((card: CardModel) => card.description),
                'Amex Cards': AccountService.getAmexCards(user).map(
                    (account: AccountModel) => account.officialName.match(/.+?(?= \()/)[0]
                ),
                Income: CashFlowService.getCurrentIncome(user),
                Expenses: CashFlowService.getCurrentExpenses(user),
                Age: age
            });
            mixpanel.people.increment('Total Logins');
        }
    }

    public static logout(user?: FullUserModel): void {
        if (!user) {
            user = StorageService.getReduxUserObject();
        }
        if (user) {
            // don't track demo user
            if (this.isDemoUser(user)) return;
            mixpanel.identify(user.email);
        }
        this.track('User Logout');
    }
}
